import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTokens } from '../../contexts/TokenContext';

const MORE_MENU_ITEMS = [
  { path: '/destiny-gpt', icon: 'ri-scales-3-line', label: 'Destiny GPT', sub: '결정 비서' },
  { path: '/blog', icon: 'ri-article-line', label: '블로그', sub: '운세 가이드' },
  { path: '/about', icon: 'ri-information-line', label: 'Destiny GPT란?', sub: '서비스 소개' },
];

export default function Navbar() {
  const location = useLocation();
  const { tokens } = useTokens();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { path: '/', label: '홈' },
    { path: '/astrology', label: '점성술' },
    { path: '/four-pillars', label: '사주팔자' },
    { path: '/celebrity-saju', label: '유명인 사주' },
    { path: '/dream', label: '꿈해몽' },
    { path: '/annual-report', label: '1년 리포트' },
    { path: '/lifetime-report', label: '평생 리포트' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isMoreActive = MORE_MENU_ITEMS.some((item) => location.pathname === item.path);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-primary-dark/95 backdrop-blur-xl shadow-lg border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative w-11 h-11 flex-shrink-0">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary-gold/30 to-amber-600/10 blur-md group-hover:blur-xl group-hover:from-primary-gold/50 transition-all duration-500"></div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#1a0f2e] to-[#0f0a1e] border border-primary-gold/60 group-hover:border-primary-gold transition-all duration-300"></div>
              <div className="absolute top-1 left-1 w-2.5 h-2.5 border-t-2 border-l-2 border-primary-gold/70 rounded-tl"></div>
              <div className="absolute top-1 right-1 w-2.5 h-2.5 border-t-2 border-r-2 border-primary-gold/70 rounded-tr"></div>
              <div className="absolute bottom-1 left-1 w-2.5 h-2.5 border-b-2 border-l-2 border-primary-gold/70 rounded-bl"></div>
              <div className="absolute bottom-1 right-1 w-2.5 h-2.5 border-b-2 border-r-2 border-primary-gold/70 rounded-br"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="text-primary-gold text-xl font-bold leading-none select-none"
                  style={{ fontFamily: '"Noto Serif KR", "Noto Serif", serif', textShadow: '0 0 10px rgba(251,191,36,0.7)' }}
                >
                  命
                </span>
              </div>
            </div>
            <div className="hidden sm:flex flex-col leading-tight gap-0.5">
              <div className="flex items-baseline gap-2">
                <span
                  className="font-black text-white tracking-[0.12em] text-lg leading-none"
                  style={{ fontFamily: '"Cinzel", "Playfair Display", "Times New Roman", serif' }}
                >
                  DESTINY
                </span>
                <span
                  className="text-primary-gold font-bold text-xs tracking-[0.35em] leading-none"
                  style={{ fontFamily: '"Cinzel", monospace' }}
                >
                  GPT
                </span>
              </div>
              <div className="h-px bg-gradient-to-r from-primary-gold/0 via-primary-gold/60 to-primary-gold/0"></div>
              <span className="text-[8px] tracking-[0.45em] text-purple-300/60 uppercase text-center">
                운명 분석 AI
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-3 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                  isActive(link.path)
                    ? 'text-primary-gold'
                    : 'text-purple-200 hover:text-white'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-primary-gold to-transparent"></span>
                )}
              </Link>
            ))}

            {/* 더보기 Dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all duration-300 rounded-lg cursor-pointer whitespace-nowrap ${
                  isMoreActive ? 'text-primary-gold' : 'text-purple-200 hover:text-white'
                }`}
              >
                더보기
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMoreOpen ? 'rotate-180' : ''}`}></i>
                </div>
                {isMoreActive && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-primary-gold to-transparent"></span>
                )}
              </button>

              {/* Dropdown Panel */}
              {isMoreOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-52 rounded-2xl border border-white/10 overflow-hidden"
                  style={{
                    background: 'rgba(10, 5, 25, 0.97)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset',
                  }}
                >
                  <div className="p-2">
                    {MORE_MENU_ITEMS.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMoreOpen(false)}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 cursor-pointer ${
                          isActive(item.path)
                            ? 'bg-primary-gold/15 text-primary-gold'
                            : 'text-purple-200 hover:bg-white/6 hover:text-white'
                        }`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/6 flex-shrink-0">
                          <i className={`${item.icon} text-sm ${isActive(item.path) ? 'text-primary-gold' : 'text-purple-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold leading-none">{item.label}</p>
                          <p className="text-xs text-purple-400/60 mt-0.5">{item.sub}</p>
                        </div>
                        {isActive(item.path) && (
                          <div className="ml-auto w-4 h-4 flex items-center justify-center">
                            <i className="ri-check-line text-primary-gold text-xs"></i>
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Token Display, 토큰내역 & CTA */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-full glass-card">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-coin-fill text-primary-gold text-lg animate-sparkle"></i>
              </div>
              <span className="text-sm sm:text-base font-semibold text-white">
                {tokens}
              </span>
            </div>

            {/* 토큰 내역 버튼 */}
            <Link
              to="/token-usage"
              className={`hidden sm:flex items-center justify-center w-9 h-9 rounded-full glass-card transition-all duration-300 cursor-pointer ${
                isActive('/token-usage') ? 'text-primary-gold border border-primary-gold/40' : 'text-purple-300 hover:text-primary-gold'
              }`}
              title="토큰 내역"
            >
              <div className="w-9 h-9 flex items-center justify-center">
                <i className="ri-history-line text-base"></i>
              </div>
            </Link>

            <Link
              to="/checkout"
              className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-purple to-primary-light rounded-full text-white text-sm font-semibold hover:shadow-lg hover:shadow-primary-purple/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line"></i>
              <span>충전</span>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg glass-card text-white hover:bg-white/10 transition-all"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-y-0 right-0 w-64 bg-primary-navy/98 backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full pt-20 pb-6 px-4">
          <div className="flex-1 space-y-1 overflow-y-auto">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                  isActive(link.path)
                    ? 'bg-primary-purple/20 text-primary-gold border border-primary-gold/30'
                    : 'text-purple-200 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className="text-base font-medium">{link.label}</span>
              </Link>
            ))}
            {/* 더보기 항목들 */}
            <div className="pt-2 mt-2 border-t border-white/8">
              <p className="text-[10px] text-purple-400/40 uppercase tracking-widest px-4 mb-2">더보기</p>
              {MORE_MENU_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-primary-purple/20 text-primary-gold border border-primary-gold/30'
                      : 'text-purple-200 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  <span className="text-base font-medium">{item.label}</span>
                </Link>
              ))}
              <Link
                to="/token-usage"
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                  isActive('/token-usage')
                    ? 'bg-primary-purple/20 text-primary-gold border border-primary-gold/30'
                    : 'text-purple-200 hover:bg-white/5 hover:text-white'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-history-line text-base"></i>
                </div>
                <span className="text-base font-medium">토큰 내역</span>
              </Link>
            </div>
          </div>

          <Link
            to="/checkout"
            onClick={() => setIsMenuOpen(false)}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary-purple to-primary-light rounded-full text-white font-semibold hover:shadow-lg hover:shadow-primary-purple/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-cart-2-line"></i>
            <span>토큰 충전하기</span>
          </Link>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        ></div>
      )}
    </nav>
  );
}
